import { Card, CardContent, Grid, Chip } from '@mui/material'
import UserAction from 'administration/components/user/actions/UserAction'
import { StyledFieldSet, StyledLegend } from 'components/StyledElements'
import Input from 'components/forms/Input'
import NumberField from 'components/forms/NumberField'
import Select from 'components/forms/Select'
import Textarea from 'components/forms/Textarea'
import { isUndefined, maxBy } from 'lodash'
import PropTypes from 'prop-types'
import React, { useMemo } from 'react'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import { SANDRE } from 'referencial/constants/ReferencialConstants'
import i18n from 'simple-react-i18n'
import useSandreList from 'utils/customHook/useSandreList'
import { favOrange } from 'utils/constants/ColorTheme'
import { CardTitle } from 'components/card/NewCard'
import Checkbox from 'components/forms/Checkbox'
import useAccountSetting from 'utils/customHook/useAccountSetting'
import { SHOW_INSEE_CODE } from 'account/constants/AccountSettingsConstants'
import useApplicationSetting from 'utils/customHook/useApplicationSetting'

const StatusFilterChip = ({
    title,
    color = 'default',
    sx,
}) => {
    return (
        <Chip
            label={title}
            size={'small'}
            color={color}
            sx={{ mr: 1, ...sx }}
        />
    )
}

StatusFilterChip.propTypes = {
    title: PropTypes.string,
    color: PropTypes.string,
    sx: PropTypes.shape({}),
}

const CatchmentForm = ({
    catchment = {},
    setCatchment = () => { },
    readMode = true,
    displayChips = false,
}) => {
    const dispatch = useDispatch()
    const {
        departments,
        userBookmarks,
        accountUser,
    } = useSelector(store => ({
        departments: store.CityReducer.departments,
        userBookmarks: store.UserReducer.userBookmarks,
        accountUser: store.AccountReducer.accountUser,
    }), shallowEqual)

    const stateFiles = useSandreList(SANDRE.ETAT_DOSSIER)
    const statePlans = useSandreList(SANDRE.ETAT_PLAN)
    const agencies = useSandreList(SANDRE.AGENCES)
    const sdages = useSandreList('CAPTAGES.SDAGE')
    const sampleTypes = useSandreList('PREL_AGRI.TYPE_PRELEVEMENT')

    const generalShowInseeCode = useApplicationSetting(SHOW_INSEE_CODE, setting => setting ? parseInt(setting) : true)
    const showInseeCode = useAccountSetting(SHOW_INSEE_CODE, setting => setting ? parseInt(setting) : generalShowInseeCode)

    const lastSDAGE = maxBy(catchment.sdages, 'startDate')

    const catchmentBookmarks = useMemo(() => {
        return userBookmarks.filter(b => b.stationType === 11).map(b => b.identifiant)
    }, [userBookmarks])

    const booleanList = [{
        id: true,
        name: i18n.yes,
    }, {
        id: false,
        name: i18n.no,
    }]

    const exemptionList = [{
        id: true,
        name: i18n.exempt,
    }, {
        id: false,
        name: i18n.notExempt,
    }]

    const isFav = catchmentBookmarks.some(code => code === catchment.code)

    const setBookmark = () => {
        if (isFav) {
            dispatch(UserAction.deleteBookmark({ identifiant: catchment.code, login: accountUser.login, stationType: 11 }))
        } else {
            dispatch(UserAction.createBookmark({ identifiant: catchment.code, login: accountUser.login, stationType: 11 }))
        }
    }

    const actions = [{
        icon: isFav ? 'star' : 'star_border',
        color: favOrange,
        onClick: setBookmark,
    }]

    const havePriority = catchment.priority ? i18n.havePriority : i18n.haveNoPriority
    const isSensitive = catchment.sensitive ? i18n.sensitive : i18n.notSensitive

    return (
        <Card>
            <CardTitle title={i18n.generalInfos} actions={actions}/>
            <CardContent>
                <Grid container columnSpacing={2} alignItems='center'>
                    <Grid size='grow'>
                        <Input
                            title={i18n.nameOfTheWork}
                            value={catchment.name}
                            readMode={readMode}
                            onChange={v => setCatchment(prev => ({ ...prev, name: v }))}
                            data-cy='name'
                        />
                    </Grid>
                    <Grid size={4}>
                        <Input
                            title={i18n.identifier}
                            value={catchment.code}
                            readMode={readMode}
                            maxLength={25}
                            onChange={v => setCatchment(prev => ({ ...prev, code: v }))}
                            data-cy='code'
                        />
                    </Grid>
                    <Grid size={4}>
                        <Select
                            label={i18n.department}
                            value={catchment.department}
                            options={departments}
                            onChange={v => setCatchment(prev => ({ ...prev, department: v }))}
                            readMode={readMode}
                            displayWithCode={showInseeCode}
                            data-cy='department'
                        />
                    </Grid>
                    <Grid size={4}>
                        <Select
                            options={agencies}
                            label={i18n.pond}
                            keyLabel={'name'}
                            nullLabel='&nbsp;'
                            onChange={v => setCatchment(prev => ({ ...prev, pond: v }))}
                            value={catchment.pond}
                            readMode={readMode}
                            data-cy='pond'
                        />
                    </Grid>
                    <Grid size={4}>
                        <Input
                            title={i18n.watermass}
                            value={catchment.meCode}
                            readMode={readMode}
                            onChange={v => setCatchment(prev => ({ ...prev, meCode: v }))}
                            data-cy='meCode'
                        />
                    </Grid>
                    <Grid size={4}>
                        <Select
                            options={sampleTypes}
                            label={i18n.originOfTheResource}
                            nullLabel='&nbsp;'
                            value={catchment.sampleType}
                            readMode={readMode}
                            onChange={v => setCatchment(prev => ({ ...prev, sampleType: v }))}
                            data-cy='sampleType'
                        />
                    </Grid>
                    <Grid size={4}>
                        <Input
                            title={i18n.catchmentFeedingAreaNumber}
                            value={catchment.aacNum}
                            readMode={readMode}
                            onChange={v => setCatchment(prev => ({ ...prev, aacNum: v }))}
                            data-cy='aacNum'
                        />
                    </Grid>
                    <Grid size={4}>
                        <Select
                            options={booleanList}
                            label={i18n.grenelleWork}
                            nullLabel='&nbsp;'
                            value={catchment.grenelleWork}
                            readMode={readMode}
                            noSort
                            onChange={v => setCatchment(prev => ({ ...prev, grenelleWork: v }))}
                            data-cy='grenelleWork'
                        />
                    </Grid>
                    <Grid size={4}>
                        <Select
                            options={sdages}
                            label={i18n.sdageWork}
                            nullLabel='&nbsp;'
                            value={lastSDAGE?.codeSDAGE}
                            readMode={true}
                            noSort
                        />
                    </Grid>
                    <Grid size={4}>
                        <Select
                            options={booleanList}
                            label={i18n.abandonedCatchment}
                            nullLabel='&nbsp;'
                            value={catchment.abandonedCatchment}
                            readMode={readMode}
                            noSort
                            onChange={v => setCatchment(prev => ({ ...prev, abandonedCatchment: v }))}
                            data-cy='abandonedCatchment'
                        />
                        <Select
                            options={booleanList}
                            label={i18n.waiverNonCompliance}
                            nullLabel='&nbsp;'
                            value={catchment.derogationConformity}
                            readMode={readMode}
                            noSort
                            onChange={v => setCatchment(prev => ({ ...prev, derogationConformity: v }))}
                            data-cy='derogationConformity'
                        />
                    </Grid>
                    <Grid size={4}>
                        <Textarea
                            title={i18n.reasonAbandonment}
                            value={catchment.causeAbandonment}
                            readMode={readMode}
                            maxLength={255}
                            onChange={v => setCatchment(prev => ({ ...prev, causeAbandonment: v }))}
                            data-cy='causeAbandonment'
                            noMargin={false}
                        />
                        <NumberField
                            title={i18n.sandreCode}
                            value={catchment.sandreCode}
                            readMode={readMode}
                            onChange={v => setCatchment(prev => ({ ...prev, sandreCode: v }))}
                            data-cy='sandreCode'
                        />
                    </Grid>
                    <Grid size={4}>
                        <Textarea
                            title={i18n.agriOrientation}
                            value={catchment.agriOrientation}
                            readMode={readMode}
                            maxLength={255}
                            onChange={v => setCatchment(prev => ({ ...prev, agriOrientation: v }))}
                            data-cy='agriOrientation'
                        />
                    </Grid>
                    { displayChips && (
                        <>
                            {!isUndefined(catchment.exempt) && (
                                <Grid size={2}>
                                    {catchment.exempt ? (
                                        <StatusFilterChip
                                            title={i18n.exempt}
                                            color='success'
                                            sx={{ padding: '0 10px' }}
                                        />
                                    ) : (
                                        <StatusFilterChip
                                            title={i18n.notExempt}
                                            color='primary'
                                            sx={{ padding: '0 10px' }}
                                        />
                                    )}
                                </Grid>
                            )}
                            { catchment.priority && (
                                <Grid size={2}>
                                    <StatusFilterChip
                                        title={havePriority}
                                        color='error'
                                        sx={{ padding: '0 10px' }}
                                    />
                                </Grid>
                            ) }
                            { catchment.sensitive && (
                                <Grid container size={2} justifyContent={catchment.priority ? 'flex-end' : 'flex-start'}>
                                    <StatusFilterChip
                                        title={isSensitive}
                                        color='warning'
                                        sx={{ padding: '0 10px' }}
                                    />
                                </Grid>
                            ) }
                        </>
                    )}
                    { !displayChips && (
                        <>
                            <Grid size={4}>
                                <Select
                                    options={exemptionList}
                                    label={i18n.exemption2}
                                    onChange={v => setCatchment(prev => ({ ...prev, exempt: v }))}
                                    value={catchment.exempt}
                                    readMode={readMode}
                                    data-cy='exemption'
                                />
                            </Grid>
                            <Grid size={4}>
                                <Checkbox
                                    label={i18n.priorityCatchment}
                                    checked={catchment.priority}
                                    onChange={v => setCatchment(prev => ({ ...prev, priority: v }))}
                                    disabled={readMode}
                                />
                            </Grid>
                            <Grid size={4}>
                                <Checkbox
                                    label={i18n.sensitiveCatchment}
                                    checked={catchment.sensitive}
                                    onChange={v => setCatchment(prev => ({ ...prev, sensitive: v }))}
                                    disabled={readMode}
                                />
                            </Grid>
                        </>
                    )}
                    <Grid size={12}>
                        <StyledFieldSet>
                            <StyledLegend>{i18n.stagesOfStudiesAndTechnicalData}</StyledLegend>
                            <Grid container columnSpacing={2}>
                                <Grid size={4}>
                                    <Textarea
                                        value={catchment.problematic}
                                        title={i18n.problematic}
                                        readMode={readMode}
                                        maxLength={255}
                                        onChange={v => setCatchment(prev => ({ ...prev, problematic: v }))}
                                        data-cy='problematic'
                                    />
                                </Grid>
                                <Grid size={4}>
                                    <Select
                                        options={stateFiles}
                                        label={i18n.aacDelineationStudy}
                                        nullLabel='&nbsp;'
                                        value={catchment.stateStudy}
                                        readMode={readMode}
                                        onChange={v => setCatchment(prev => ({ ...prev, stateStudy: v }))}
                                        data-cy='stateStudy'
                                    />
                                    <Select
                                        options={stateFiles}
                                        label={i18n.territorialDiagnosisOfPressures}
                                        nullLabel='&nbsp;'
                                        value={catchment.stateDiagnosticPressure}
                                        readMode={readMode}
                                        onChange={v => setCatchment(prev => ({ ...prev, stateDiagnosticPressure: v }))}
                                        data-cy='stateDiagnosticPressure'
                                    />
                                </Grid>
                                <Grid size={4}>
                                    <Select
                                        options={statePlans}
                                        label={i18n.actionPlan}
                                        nullLabel='&nbsp;'
                                        value={catchment.stateActionPlan}
                                        readMode={readMode}
                                        onChange={v => setCatchment(prev => ({ ...prev, stateActionPlan: v }))}
                                        data-cy='stateActionPlan'
                                    />
                                    <NumberField
                                        title={i18n.aacArea}
                                        value={catchment.aacArea}
                                        readMode={readMode}
                                        onChange={v => setCatchment(prev => ({ ...prev, aacArea: v }))}
                                        data-cy='aacArea'
                                    />
                                </Grid>
                            </Grid>
                        </StyledFieldSet>
                    </Grid>
                </Grid>
            </CardContent>
        </Card>

    )
}

CatchmentForm.propTypes = {
    catchment: PropTypes.shape({}),
    setCatchment: PropTypes.func,
    readMode: PropTypes.bool,
    displayChips: PropTypes.bool,
}

export default CatchmentForm